import { FC, useEffect, useRef } from 'react'
import { ScrollBarContainer } from 'src/components/ScrollBarContainer'
import { getNumberOrZero } from 'src/utils/getNumberOrZero'
import { TotalMentionPercentage } from 'src/components/TotalMentionPercentage'
import en from 'src/constants/en'
import { AnalyticsFetchQuickDetailsResponse } from 'src/services/__generated__/api'
import { PositivityLegend } from 'src/components/PositivityLegend'
import { PositivityTypes } from 'src/components/PositivityLegend/enums/positivityTypes.enum'
import { useGetInformationTooltipsQuery } from 'src/reactQuery/useGetInformationTooltips'
import { SectionTitle } from 'src/components/SectionTitle'
import { Skeleton } from 'src/components/Skeleton'
import styles from './quickDetailsSection.module.scss'
import { SourcesList } from './SourcesList'
import { QuickDetailsChart } from './QuickDetailsChart'

const LIST_ITEM_COLLAPSED_WIDTH = 209

// Mirrors one real card: the donut is 124x124 (quickDetailsChart.module.scss
// .quickDetailsChart), the name line is 18px/21px (.cardName) and the
// percentage block below it is an 18px/21px value over a 14px/16px label
// (totalMentionPercentage.module.scss .details / .label).
const CHART_SIZE = 124
const CARD_NAME_HEIGHT = 21
const MENTION_VALUE_HEIGHT = 21
const MENTION_LABEL_HEIGHT = 16
const CARD_SKELETON_COUNT = 3

interface QuickDetailsSectionProps {
  activeItemIndex: number
  chartData?: AnalyticsFetchQuickDetailsResponse
  isLoading?: boolean
  setScrollQuickDetailsToIndexCallback: (
    callback: (index: number) => void
  ) => void
  updateActiveItemIndex: (index: number) => void
}

export const QuickDetailsSection: FC<QuickDetailsSectionProps> = ({
  activeItemIndex,
  chartData,
  isLoading,
  setScrollQuickDetailsToIndexCallback,
  updateActiveItemIndex,
}) => {
  const { data: informationTooltips } = useGetInformationTooltipsQuery()

  const scrollableDivRef = useRef<HTMLDivElement | null>(null)

  const scrollQuickDetailsToIndex = (index: number) => {
    scrollableDivRef.current?.scrollTo({
      behavior: 'smooth',
      left: index * LIST_ITEM_COLLAPSED_WIDTH,
    })
  }

  useEffect(() => {
    setScrollQuickDetailsToIndexCallback(scrollQuickDetailsToIndex)
  }, [])

  const overallMentions =
    chartData?.reduce((mentionCount, campaign) => {
      return getNumberOrZero(campaign?.total) + mentionCount
    }, 0) || 0

  // First load only: there is genuinely nothing to show yet. A refetch keeps
  // the existing cards on screen rather than flashing back to placeholders.
  const isInitialLoading = Boolean(isLoading) && !chartData

  // Without the isInitialLoading guard this bailed out to null while the first
  // request was in flight, so the whole 770px section collapsed and then jumped
  // open when the data landed. Empty state (loaded, but no data) still returns
  // null, which is unchanged.
  if (!chartData && !isInitialLoading) {
    return null
  }

  return (
    <div className={styles.quickDetailsSection}>
      <SectionTitle
        isLoading={isLoading}
        title={en.quickDetails}
        tooltipContent={informationTooltips?.quickDetails}
      />

      <div className={styles.quickDetailsSectionInner}>
        <ScrollBarContainer isHorizontal ref={scrollableDivRef}>
          <div className={styles.scrollBarContainer}>
            {isInitialLoading &&
              Array.from({ length: CARD_SKELETON_COUNT }).map((_, index) => (
                <div
                  className={styles.cardWrapper}
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                >
                  <div
                    className={styles.card}
                    style={{ width: `${LIST_ITEM_COLLAPSED_WIDTH}px` }}
                  >
                    <div className={styles.chartPlaceholder}>
                      <Skeleton circle width={CHART_SIZE} />
                    </div>

                    <div className={styles.cardName}>
                      <Skeleton height={CARD_NAME_HEIGHT} width={140} />
                    </div>

                    <div className={styles.bottomDetails}>
                      <Skeleton height={MENTION_VALUE_HEIGHT} width={112} />
                    </div>

                    <Skeleton height={MENTION_LABEL_HEIGHT} width={88} />
                  </div>
                </div>
              ))}

            {chartData?.map((campaign, index) => {
              const isActive = activeItemIndex === index

              const { name, total, topSources } = campaign

              return (
                <div
                  className={styles.cardWrapper}
                  key={`${name} - ${total}`}
                  onClick={() => updateActiveItemIndex(index)}
                >
                  <div
                    className={styles.card}
                    style={{
                      width: `${LIST_ITEM_COLLAPSED_WIDTH}px`,
                    }}
                  >
                    <QuickDetailsChart
                      campaign={campaign}
                      isActive={isActive}
                    />

                    <div className={styles.cardName}>{name}</div>

                    <TotalMentionPercentage
                      overallMentions={overallMentions}
                      totalMentions={getNumberOrZero(total)}
                    />
                  </div>

                  {isActive && (
                    <SourcesList
                      topSources={
                        topSources?.map(
                          (topSource) => topSource.source ?? ''
                        ) ?? []
                      }
                    />
                  )}
                </div>
              )
            })}
          </div>
        </ScrollBarContainer>
      </div>

      <div className={styles.footer}>
        <PositivityLegend
          types={[
            PositivityTypes.POSITIVE,
            PositivityTypes.NEUTRAL,
            PositivityTypes.NEGATIVE,
          ]}
        />
      </div>
    </div>
  )
}
